// src/lib/metrics/risk.ts
/**
 * Risk metrics - VaR, ES, drawdown calculations
 */

import type { Series } from './types';
import { CONFIDENCE_LEVELS } from './types';
import { filterValid } from './primitives';

// ============================================================================
// VALUE AT RISK (VaR)
// ============================================================================

/**
 * Calculate historical VaR at specified confidence level
 * VaR represents the maximum expected loss at the given confidence level
 * 
 * @param returns - Array of returns as decimals
 * @param confidence - Confidence level (e.g., 0.95 for 95%)
 * @returns VaR as decimal (negative number representing loss threshold)
 */
export function valueAtRisk(
  returns: Series,
  confidence: number = CONFIDENCE_LEVELS.VAR_95
): number | null {
  const valid = filterValid(returns);
  if (valid.length === 0) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  const idx = Math.floor((1 - confidence) * sorted.length);
  const clampedIdx = Math.max(0, Math.min(sorted.length - 1, idx));
  
  return sorted[clampedIdx] ?? null;
}

/**
 * Calculate VaR as a positive loss number (more intuitive for display)
 * @returns Positive number representing loss threshold
 */
export function valueAtRiskLoss(
  returns: Series,
  confidence: number = CONFIDENCE_LEVELS.VAR_95
): number | null {
  const var_ = valueAtRisk(returns, confidence);
  return var_ !== null ? Math.abs(var_) : null;
}

// ============================================================================
// EXPECTED SHORTFALL (ES / CVaR)
// ============================================================================

/**
 * Calculate Expected Shortfall (Conditional VaR)
 * Average loss in the tail beyond VaR threshold
 * 
 * @param returns - Array of returns as decimals
 * @param confidence - Confidence level (e.g., 0.95 for 95%)
 * @returns ES as decimal (negative number representing average tail loss)
 */
export function expectedShortfall(
  returns: Series,
  confidence: number = CONFIDENCE_LEVELS.VAR_95
): number | null {
  const valid = filterValid(returns);
  if (valid.length === 0) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  const cutoffIdx = Math.floor((1 - confidence) * sorted.length);
  
  if (cutoffIdx <= 0) {
    return sorted[0] ?? null; // Return worst case if no tail
  }
  
  const tail = sorted.slice(0, cutoffIdx);
  if (tail.length === 0) return sorted[0] ?? null;
  
  const avgTailLoss = tail.reduce((a, b) => a + b, 0) / tail.length;
  return avgTailLoss;
}

/**
 * Calculate ES as a positive loss number (more intuitive for display)
 */
export function expectedShortfallLoss(
  returns: Series,
  confidence: number = CONFIDENCE_LEVELS.VAR_95
): number | null {
  const es = expectedShortfall(returns, confidence);
  return es !== null ? Math.abs(es) : null;
}

// ============================================================================
// DRAWDOWN
// ============================================================================

/**
 * Calculate drawdown series from prices
 * Drawdown = (current - peak) / peak
 * 
 * @param prices - Array of prices
 * @returns Array of drawdowns as decimals (negative values)
 */
export function drawdownSeries(prices: Series): number[] {
  const valid = filterValid(prices);
  if (valid.length === 0) return [];
  
  const drawdowns: number[] = [];
  let peak = -Infinity;
  
  for (const price of valid) {
    peak = Math.max(peak, price);
    const dd = peak > 0 ? (price / peak) - 1 : 0;
    drawdowns.push(dd);
  }
  
  return drawdowns;
}

/**
 * Calculate drawdown series from returns (builds equity curve first)
 */
export function drawdownSeriesFromReturns(returns: Series): number[] {
  const valid = filterValid(returns);
  if (valid.length === 0) return [];
  
  // Build equity curve
  const equity: number[] = [1];
  let current = 1;
  for (const r of valid) {
    current *= (1 + r);
    equity.push(current);
  }
  
  return drawdownSeries(equity);
}

/**
 * Calculate maximum drawdown from prices
 * @returns Maximum drawdown as decimal (negative value)
 */
export function maxDrawdown(prices: Series): number | null {
  const dd = drawdownSeries(prices);
  if (dd.length === 0) return null;
  
  return Math.min(...dd);
}

/**
 * Calculate maximum drawdown from returns
 */
export function maxDrawdownFromReturns(returns: Series): number | null {
  const dd = drawdownSeriesFromReturns(returns);
  if (dd.length === 0) return null;
  
  return Math.min(...dd);
}

/**
 * Calculate current drawdown from prices
 * @returns Current drawdown as decimal (negative or zero)
 */
export function currentDrawdown(prices: Series): number | null {
  const dd = drawdownSeries(prices);
  if (dd.length === 0) return null;
  
  return dd[dd.length - 1] ?? null;
}

/**
 * Calculate drawdown duration (number of periods in drawdown)
 * @returns Current drawdown duration in periods
 */
export function drawdownDuration(prices: Series): number {
  const dd = drawdownSeries(prices);
  if (dd.length === 0) return 0;
  
  let duration = 0;
  for (let i = dd.length - 1; i >= 0; i--) {
    if (dd[i]! < 0) {
      duration++;
    } else {
      break;
    }
  }
  
  return duration;
}

/**
 * Calculate average drawdown duration
 */
export function averageDrawdownDuration(prices: Series): number | null {
  const dd = drawdownSeries(prices);
  if (dd.length === 0) return null;
  
  const durations: number[] = [];
  let currentDuration = 0;
  
  for (const d of dd) {
    if (d < 0) {
      currentDuration++;
    } else if (currentDuration > 0) {
      durations.push(currentDuration);
      currentDuration = 0;
    }
  }
  
  // Include final ongoing drawdown
  if (currentDuration > 0) {
    durations.push(currentDuration);
  }
  
  if (durations.length === 0) return 0;
  return durations.reduce((a, b) => a + b, 0) / durations.length;
}

/**
 * Calculate time to recovery from drawdown (number of periods)
 * @returns Median recovery time across all drawdowns
 */
export function medianRecoveryTime(prices: Series): number | null {
  const valid = filterValid(prices);
  if (valid.length < 2) return null;
  
  const recoveries: number[] = [];
  let peak = -Infinity;
  let drawdownStart = -1;
  
  for (let i = 0; i < valid.length; i++) {
    const price = valid[i]!;
    
    if (price > peak) {
      // New peak - if we were in drawdown, record recovery time
      if (drawdownStart >= 0) {
        recoveries.push(i - drawdownStart);
      }
      peak = price;
      drawdownStart = -1;
    } else if (drawdownStart === -1) {
      // Start of new drawdown
      drawdownStart = i;
    }
  }
  
  if (recoveries.length === 0) {
    // Still in drawdown, return ongoing duration
    return drawdownStart >= 0 ? valid.length - 1 - drawdownStart : 0;
  }
  
  // Return median
  const sorted = [...recoveries].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? null;
}

// ============================================================================
// VOLATILITY OF VOLATILITY (VoV)
// ============================================================================

/**
 * Calculate volatility of volatility
 * Standard deviation of rolling volatility
 * 
 * @param returns - Array of returns
 * @param volWindow - Window for rolling volatility calculation
 * @param minPoints - Minimum points for valid volatility
 */
export function volatilityOfVolatility(
  returns: Series,
  volWindow: number = 30,
  minPoints: number = 20
): number | null {
  const valid = filterValid(returns);
  if (valid.length < volWindow) return null;
  
  // Calculate rolling volatility
  const rollingVols: number[] = [];
  
  for (let i = volWindow - 1; i < valid.length; i++) {
    const window = valid.slice(i - volWindow + 1, i + 1);
    if (window.length >= minPoints) {
      const m = window.reduce((a, b) => a + b, 0) / window.length;
      const v = window.reduce((acc, r) => acc + Math.pow(r - m, 2), 0) / (window.length - 1);
      rollingVols.push(Math.sqrt(v));
    }
  }
  
  if (rollingVols.length < 2) return null;
  
  // Calculate std dev of rolling vols
  const volMean = rollingVols.reduce((a, b) => a + b, 0) / rollingVols.length;
  const volVariance = rollingVols.reduce((acc, v) => acc + Math.pow(v - volMean, 2), 0) / (rollingVols.length - 1);
  
  return Math.sqrt(volVariance);
}

// ============================================================================
// TAIL RISK METRICS
// ============================================================================

/**
 * Calculate crash sensitivity
 * Average token return on worst N benchmark days
 * 
 * @param returns - Asset returns
 * @param benchmarkReturns - Benchmark returns
 * @param worstDays - Number of worst benchmark days to consider
 */
export function crashSensitivity(
  returns: Series,
  benchmarkReturns: Series,
  worstDays: number = 10
): number | null {
  const assetRets = filterValid(returns);
  const benchRets = filterValid(benchmarkReturns);
  
  const n = Math.min(assetRets.length, benchRets.length);
  if (n < worstDays) return null;
  
  // Align from end
  const assetAligned = assetRets.slice(-n);
  const benchAligned = benchRets.slice(-n);
  
  // Find indices of worst benchmark days
  const indexed = benchAligned.map((v, i) => ({ v, i }));
  const sorted = [...indexed].sort((a, b) => a.v - b.v);
  const worstIndices = sorted.slice(0, worstDays).map(x => x.i);
  
  // Average asset return on those days
  const worstDayReturns = worstIndices
    .map(i => assetAligned[i])
    .filter((v): v is number => v !== undefined && Number.isFinite(v));
  
  if (worstDayReturns.length === 0) return null;
  
  return worstDayReturns.reduce((a, b) => a + b, 0) / worstDayReturns.length;
}

/**
 * Calculate tail ratio
 * Ratio of average gains to average losses in tails
 */
export function tailRatio(
  returns: Series,
  percentile: number = 0.05
): number | null {
  const valid = filterValid(returns);
  if (valid.length < 20) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  const tailSize = Math.max(1, Math.floor(valid.length * percentile));
  
  const leftTail = sorted.slice(0, tailSize);
  const rightTail = sorted.slice(-tailSize);
  
  const avgLoss = Math.abs(leftTail.reduce((a, b) => a + b, 0) / leftTail.length);
  const avgGain = rightTail.reduce((a, b) => a + b, 0) / rightTail.length;
  
  if (avgLoss === 0) return null;
  return avgGain / avgLoss;
}

